/** First sign-in of a local account: enrol an authenticator app (mandatory TOTP, ADR-0004). */
import { useEffect, useState, type FormEvent } from 'react';
import { Banner, Button, Field, Input, Spinner } from '../../components';
import { authErrorKey, type TotpEnrolment } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { useServices } from '../../app/services';
import { AuthLayout } from './AuthLayout';

/** Enrolment is done (or was already) but this session's token predates it. */
const REAUTH = 'ui.mfa.signInAgain';

export function MfaEnrolPage({ onDone }: { onDone: () => Promise<void> | void }) {
  const { auth } = useServices();
  const { t } = useI18n();
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    auth.startTotpEnrolment().then(setEnrolment, (e) => setError(authErrorKey(e)));
  }, [auth]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!enrolment) return;
    setBusy(true);
    setError(null);
    try {
      // Refreshes the token too, so the API sees the new second factor.
      await auth.finishTotpEnrolment(enrolment, code);
      await onDone();
    } catch (err) {
      setError(authErrorKey(err));
    } finally {
      setBusy(false);
    }
  };

  const grouped = enrolment?.secret.replace(/(.{4})/g, '$1 ').trim();

  return (
    <AuthLayout>
      <h2 className="mb-2 text-xl font-bold">{t('ui.mfa.title')}</h2>
      <p className="mb-4 text-muted">{t('ui.mfa.intro')}</p>
      {error && (
        <Banner tone={error === REAUTH ? 'info' : 'danger'} className="mb-4">
          {t(error)}
        </Banner>
      )}
      {error === REAUTH ? (
        <Button size="lg" block onClick={() => auth.signOut()}>
          {t('ui.offline.signInAgain')}
        </Button>
      ) : !enrolment ? (
        !error && <Spinner />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <ol className="flex list-decimal flex-col gap-3 ps-5 text-sm">
            <li>
              {t('ui.mfa.step1')}{' '}
              <a href={enrolment.otpauthUrl} className="font-semibold text-brand underline">
                {t('ui.mfa.openApp')}
              </a>
            </li>
            <li>
              {t('ui.mfa.step2')}
              <code className="mt-2 block select-all rounded-xl bg-surface-2 p-3 text-center font-mono text-lg tracking-wider">{grouped}</code>
            </li>
            <li>{t('ui.mfa.step3')}</li>
          </ol>
          <Field label={t('ui.auth.code')}>
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={enrolment.codeLength}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-[0.4em]"
              />
            )}
          </Field>
          <Button type="submit" size="lg" block loading={busy} disabled={code.length < enrolment.codeLength}>
            {t('ui.mfa.confirm')}
          </Button>
          <Button variant="ghost" onClick={() => auth.signOut()}>
            {t('ui.profile.signOut')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
